import { Suspense, lazy } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, PlayCircle, ShieldCheck, Zap } from "lucide-react";

const HeroScene = lazy(() => import("@/components/three/HeroScene"));

// Free stock IT boardroom / meeting video. MP4 works cross-browser.
// Swap HERO_VIDEO for /videos/hero.mp4 if you self-host the clip in public/videos/.
const HERO_VIDEO =
  "https://cdn.coverr.co/videos/coverr-team-meeting-in-a-boardroom-4475/1080p.mp4";
const HERO_POSTER =
  "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?auto=format&fit=crop&w=1920&q=80";

export default function Hero() {
  return (
    <section
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden isolate flex items-center pt-24 md:pt-28"
    >
      {/* Layer 1: Background video */}
      <video
        className="absolute inset-0 w-full h-full object-cover"
        autoPlay
        muted
        loop
        playsInline
        poster={HERO_POSTER}
        preload="metadata"
        data-testid="hero-video"
      >
        <source src={HERO_VIDEO} type="video/mp4" />
      </video>

      {/* Layer 2: Dark gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-950/95 via-slate-950/85 to-slate-950/95" />
      <div className="absolute inset-0 bg-[radial-gradient(1100px_600px_at_80%_20%,rgba(139,92,246,0.18),transparent_60%),radial-gradient(900px_500px_at_10%_80%,rgba(0,229,255,0.14),transparent_60%)]" />

      {/* Layer 3: Grid */}
      <div className="absolute inset-0 grid-bg opacity-40 mask-fade-b" />

      {/* Layer 4: 3D scene */}
      <div className="absolute inset-0 opacity-90">
        <Suspense fallback={null}>
          <HeroScene />
        </Suspense>
      </div>

      {/* Layer 5: Content */}
      <div className="relative z-10 mx-auto max-w-7xl px-6 md:px-10 w-full grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-8">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full glass"
            data-testid="hero-eyebrow"
          >
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-cyan-400" />
            </span>
            <span className="eyebrow !text-cyan-300">Now enrolling Q1 platform engagements</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.75, delay: 0.1 }}
            className="mt-6 font-display text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter text-white leading-[0.95]"
            data-testid="hero-headline"
          >
            Engineer resilient <br />
            <span className="text-gradient">cloud platforms.</span>
            <br />
            Ship secure software faster.
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.25 }}
            className="mt-7 max-w-2xl text-slate-300/90 text-base md:text-lg leading-relaxed"
            data-testid="hero-subheadline"
          >
            DevOps Pro Cloud Inc. designs, migrates and operates production-grade platforms
            on AWS, Azure and GCP — with DevSecOps built-in, Terraform &amp; Terragrunt at the
            core, and Splunk-grade observability. Based in King of Prussia, PA.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="mt-9 flex flex-wrap items-center gap-3"
          >
            <Link
              to="/contact"
              data-testid="hero-cta-primary"
              className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-cyan-400 text-slate-950 font-semibold hover:bg-cyan-300 transition-all shadow-[0_0_36px_rgba(0,229,255,0.35)]"
            >
              Book a free consultation
              <ArrowUpRight size={17} className="group-hover:rotate-45 transition-transform" />
            </Link>
            <Link
              to="/services"
              data-testid="hero-cta-secondary"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-white/15 bg-white/[0.03] backdrop-blur text-white font-medium hover:bg-white/[0.08] transition"
            >
              <PlayCircle size={17} className="text-cyan-300" />
              Explore services
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-xs text-slate-400 font-mono-plex tracking-wider"
          >
            <div className="flex items-center gap-2"><ShieldCheck size={14} className="text-cyan-400" /> HIPAA · SOC2 · PCI ready</div>
            <div className="flex items-center gap-2"><Zap size={14} className="text-violet-400" /> Zero-downtime cutovers</div>
            <div className="hidden sm:flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 shadow-[0_0_10px_#34d399]" /> 24/7 platform on-call
            </div>
          </motion.div>
        </div>

        {/* Right side stat cards */}
        <div className="lg:col-span-4 hidden lg:flex flex-col gap-4">
          {[
            { k: "-42%", v: "average cloud spend reduction", accent: "text-cyan-300" },
            { k: "9.4×", v: "peak-day throughput scaled", accent: "text-violet-300" },
            { k: "6.1×", v: "faster deploys with GitOps", accent: "text-fuchsia-300" },
          ].map((s, i) => (
            <motion.div
              key={s.k}
              initial={{ opacity: 0, x: 24 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.5 + i * 0.12 }}
              className="glass rounded-2xl px-5 py-4 flex items-baseline gap-4"
              data-testid={`hero-stat-${i}`}
            >
              <div className={`font-display text-3xl font-black tracking-tight ${s.accent}`}>{s.k}</div>
              <div className="text-slate-300 text-sm">{s.v}</div>
            </motion.div>
          ))}
          <div className="mt-2 relative rounded-2xl overflow-hidden border border-white/10">
            <img
              src="https://images.unsplash.com/photo-1531403009284-440f080d1e12?auto=format&fit=crop&w=800&q=80"
              alt="Engineering workflow"
              className="w-full h-40 object-cover opacity-80"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/40 to-transparent" />
            <div className="absolute bottom-3 left-4 right-4">
              <div className="eyebrow">Live workflow</div>
              <div className="text-sm text-white font-medium mt-1">GitOps pipeline · self-healing infra</div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom fade */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-32 bg-gradient-to-b from-transparent to-[#020617]" />
    </section>
  );
}
